import { useInfiniteQuery } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { UrlHelper } from '@/utils/UrlHelper';
import { FaqListResponseDto } from '@/features/faq/types/faq-dto';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {
    take?: number;
    skip?: number;
} & ApiHookProps;

export const useFaqListApi = ({ searchText, take = 20, skip }: Props = {}) => {
    const axiosInstance = useAxiosAuth();

    const {
        data: faqListData,
        isLoading: isLoadingFaqListData,
        isError: isErrorFaqListData,
        fetchNextPage: fetchFaqListDataNextPage,
        hasNextPage: hasFaqListDataNextPage,
        isFetchingNextPage: isFetchingFaqListDataNextPage
    } = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.features.faq.queryKeys.list,
            searchText, take, skip
        ],
        queryFn: async ({ pageParam }): Promise<FaqListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.faq.endpoints.list, {
                searchText, take, skip: pageParam
            });
            const response = await axiosInstance.get<FaqListResponseDto>(url); 
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.flatMap(page => page.items).length;
            return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
        },
        initialPageParam: skip ?? 0,
    });

    const fetchedData = faqListData?.pages.flatMap((page) => page.items ?? []);


    return {   
        faqListData: fetchedData,
        isLoadingFaqListData,
        isErrorFaqListData,
        fetchFaqListDataNextPage,
        hasFaqListDataNextPage,
        isFetchingFaqListDataNextPage
    }
}